import {
  Component,
  ElementRef,
  ViewChild,
  signal,
  effect,
  inject,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import {
  authorityIds,
  CHAT_MESSAGES,
  CHAT_POPUP_SIDE,
  ETF_ROUTE_PREFIX,
  operations,
  OSMA_ROUTE_PREFIX,
} from '@app/enums';
import { RequestService } from '@app/_services/request.service';
import { MarkdownModule } from 'ngx-markdown';
import { LoaderComponent } from '@app/common/loader/loader.component';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { Router } from '@angular/router';
import { UtilityService } from '@app/_services/utility.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

@Component({
  selector: 'app-chat-popup',
  standalone: true,
  imports: [
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    MarkdownModule,
    LoaderComponent,
    NzFlexModule,
  ],
  templateUrl: './chat-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./chat-popup.component.scss'],
})
export class ChatPopupComponent {
  private router = inject(Router);
  private utility = inject(UtilityService);
  open = signal(false);
  loading = signal(false);
  input = '';
  messages = signal<ChatMessage[]>(this.loadMessages());
  fabX = 0;
  isFabLeft = false;
  showScrollButton = false;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartFabX = 0;
  private hasDragged = false;
  private readonly FAB_WIDTH = 55;
  private readonly SIDE_GAP = 20;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;

  constructor(private REQ: RequestService) {
    effect(() => {
      const messages = this.messages();
      //don't store the default message
      messages.length > 1 && this.utility.setLocalStorage(CHAT_MESSAGES, JSON.stringify(messages));
      queueMicrotask(() => this.scrollToBottom());
    });
  }

  ngOnInit(): void {
    if (this.utility.getLocalStorage(CHAT_POPUP_SIDE) === 'left') {
      this.setLeftPosition();
    } else {
      this.setRightPosition();
    }
  }

  private loadMessages(): ChatMessage[] {
    const user = this.utility.getLoggedInUser();
    let userName = '';
    if (user) {
      userName = `${user.first_name} ${user.last_name}`;
    }
    const initialMessage: ChatMessage[] = [
      {
        role: 'assistant',
        content: this.getDefaultChatMessage(userName),
      },
    ];
    try {
      const stored = this.utility.getLocalStorage(CHAT_MESSAGES);
      if (!stored) {
        return initialMessage;
      }
      const messages = JSON.parse(stored);
      if (!Array.isArray(messages)) {
        return initialMessage;
      }
      return messages;
    } catch (error) {
      console.error('Failed to load chat messages:', error);
      return initialMessage;
    }
  }

  toggle() {
    this.open.update((v) => !v);
    if (this.open()) {
      this.scrollToBottom();
    }
  }

  close() {
    this.open.set(false);
  }

  send() {
    const text = this.input.trim();
    if (!text || this.loading()) return;
    this.messages.update((list) => [...list, { role: 'user', content: text }]);
    this.input = '';
    this.loading.set(true);
    const qry = {
      params: {
        question: text,
      },
      user_data: {
        authority: authorityIds.AI,
        sub_authority: '',
        operation: operations.SEARCH,
        data: 'NA',
        format: 'json',
      },
    };

    this.REQ.post('matrix/ask', qry).subscribe({
      next: (res) => {
        this.loading.set(false);
        this.messages.update((list) => [
          ...list,
          {
            role: 'assistant',
            content: this.transformAnswer(res),
          },
        ]);
      },
      error: () => {
        this.loading.set(false);
        this.messages.update((list) => [
          ...list,
          {
            role: 'assistant',
            content: 'Something went wrong.',
          },
        ]);
      },
    });
  }

  transformAnswer(response: any): string {
    let answer = response.answer;
    for (const entity of response.entities) {
      if (entity.search_string) {
        const aliases = entity.search_string
          .split('|')
          .map((value: string) => value.trim())
          .filter(Boolean);

        const link = this.getEntityLink(entity.id_type);
        if (!link) {
          continue;
        }

        // Use the longest alias first.
        // This prevents "PSTP" from interfering with longer matches.
        aliases.sort((a: any, b: any) => b.length - a.length);

        for (const alias of aliases) {
          // const escapedAlias = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // const regex = new RegExp(`(?<![\\w-])${escapedAlias}(?![\\w-])`, 'g');
          const regex = new RegExp(alias, 'gi');
          answer = answer.replace(regex, `[${alias}](entity:${entity.id_type}:${entity.id_value})`);
        }
      }
    }

    return answer;
  }

  onEntityClick(event: MouseEvent): void {
    const target = event.target as HTMLAnchorElement;
    if (target.tagName !== 'A') {
      return;
    }

    const href = target.getAttribute('href');
    if (!href?.startsWith('entity:')) {
      return;
    }

    event.preventDefault();
    const [, type, id] = href.split(':');
    const link = this.getEntityLink(type);
    this.router.navigate([link, id]);
  }

  getEntityLink(type: any): string | null {
    switch (type) {
      case 'inst_id':
        return `${ETF_ROUTE_PREFIX}/instrument/details`;

      case 'entity_id':
        return `${ETF_ROUTE_PREFIX}/entity/info`;

      case 'portfolio_id':
        return `${ETF_ROUTE_PREFIX}/portfolio/details`;

      case 'advisor_id':
        return `${OSMA_ROUTE_PREFIX}/advisor/advisor-details`;

      case 'client_id':
        return `${OSMA_ROUTE_PREFIX}/clients/details`;

      case 'strategy_id':
        return `${OSMA_ROUTE_PREFIX}/strategies`;

      default:
        return null;
    }
  }

  scrollToBottom(event?: MouseEvent): void {
    event?.stopPropagation();
    requestAnimationFrame(() => {
      const el = this.scrollContainer?.nativeElement;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth',
      });
      this.showScrollButton = false;
    });
  }

  onChatScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const threshold = 80;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    this.showScrollButton = !isAtBottom;
  }

  private setRightPosition(): void {
    this.fabX = window.innerWidth - this.FAB_WIDTH - this.SIDE_GAP;
    this.isFabLeft = false;
  }

  private setLeftPosition(): void {
    this.fabX = this.SIDE_GAP;
    this.isFabLeft = true;
  }

  startDrag(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartX = event.clientX;
    this.dragStartFabX = this.fabX;
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onDrag(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }
    const diffX = event.clientX - this.dragStartX;
    if (Math.abs(diffX) > 5) {
      this.hasDragged = true;
    }
    const maxX = window.innerWidth - this.FAB_WIDTH - this.SIDE_GAP;
    const newX = this.dragStartFabX + diffX;
    this.fabX = Math.max(this.SIDE_GAP, Math.min(newX, maxX));
  }

  @HostListener('document:pointerup')
  stopDrag(): void {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;

    if (!this.hasDragged) {
      return;
    }

    this.onResize();
    this.utility.setLocalStorage(CHAT_POPUP_SIDE, this.isFabLeft ? 'left' : 'right', 365);
    this.hasDragged = false;
  }

  @HostListener('window:resize')
  onResize(): void {
    const maxX = window.innerWidth - this.FAB_WIDTH - this.SIDE_GAP;
    const distanceFromLeft = this.fabX;
    const distanceFromRight = maxX - this.fabX;
    if (distanceFromLeft < distanceFromRight) {
      this.fabX = this.SIDE_GAP;
      this.isFabLeft = true;
    } else {
      this.fabX = maxX;
      this.isFabLeft = false;
    }
  }

  onFabClick(event: MouseEvent): void {
    if (this.hasDragged) {
      event.preventDefault();
      event.stopPropagation();
      this.hasDragged = false;
      return;
    }
    this.toggle();
  }

  getDefaultChatMessage(userName: string): string {
    const hour = new Date().getHours();
    let greeting: string;
    if (hour < 12) {
      greeting = 'Good morning';
    } else if (hour < 18) {
      greeting = 'Good afternoon';
    } else {
      greeting = 'Good evening';
    }

    const messages = [
      `${greeting}, ${userName}! How can I help you today?`,
      `${greeting}, ${userName}. What can I help you with?`,
      `Hi ${userName}! What can I help you with today?`,
      `Hi ${userName}! I’m Matrix. Ask me anything.`,
      `${greeting}, ${userName}! I’m Matrix. How can I help?`,
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  }
}
